// /opt/isp_erp/frontend/src/pages/tickets/CreateCambioEquipoTicket.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton as MuiIconButton
} from '@mui/material';
import {
  Cancel,
  SwapHoriz,
  Person,
  Add,
  Delete,
  ExpandMore,
  Inventory,
  Search
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ticketsAPI } from '../../api/tickets.api';
import { getCustomers } from '../../api/clientes.api';
import { warehouseAPI } from '../../api/warehouse.api';
import api from '../../api/axios';
import { scheduleAPI } from '../../api/schedule.api';

const CreateCambioEquipoTicket = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [cargandoDatos, setCargandoDatos] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [equiposInstalados, setEquiposInstalados] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [selectedTecnico, setSelectedTecnico] = useState(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // ✅ Estados para equipos viejos (los que se reemplazan)
  const [equiposViejos, setEquiposViejos] = useState([]);
  const [equiposViejosDisponibles, setEquiposViejosDisponibles] = useState([]);
  const [selectedEquipoViejo, setSelectedEquipoViejo] = useState(null);
  const [searchEquipoViejo, setSearchEquipoViejo] = useState('');
  const [expandedViejos, setExpandedViejos] = useState(false);

  // Estados para verificación de disponibilidad
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [isAvailable, setIsAvailable] = useState(null);
  const [availabilityError, setAvailabilityError] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);

  const [formData, setFormData] = useState({
    customer_id: '',
    assigned_technician_id: '',
    motivo_cambio: '',
    title: '',
    description: '',
    priority: 'media',
    contact_phone: '',
    contact_phone_alt: '',
    scheduled_date: ''
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  useEffect(() => {
    if (formData.customer_id) {
      const cliente = clientes.find(c => c.id === parseInt(formData.customer_id));
      setSelectedCliente(cliente);
      const instalados = equiposInstalados.filter(e => e.customer_id === parseInt(formData.customer_id));
      setEquiposViejosDisponibles(instalados);
    } else {
      setSelectedCliente(null);
      setEquiposViejosDisponibles([]);
    }
  }, [formData.customer_id, clientes, equiposInstalados]);

  useEffect(() => {
    if (formData.assigned_technician_id) {
      const tecnico = tecnicos.find(t => t.id === parseInt(formData.assigned_technician_id));
      setSelectedTecnico(tecnico);
      setIsAvailable(null);
      setAvailabilityError('');
      setAvailableSlots([]);
    } else {
      setSelectedTecnico(null);
    }
  }, [formData.assigned_technician_id, tecnicos]);

  const cargarDatos = async () => {
    try {
      const [clientesData, usuariosResponse, equiposRes] = await Promise.all([
        getCustomers(),
        api.get('/users'),
        warehouseAPI.listarEquipos({ estado: 'INSTALADO' })
      ]);

      setClientes(clientesData || []);
      const todosUsuarios = usuariosResponse.data || [];
      setTecnicos(todosUsuarios);
      setEquiposInstalados(equiposRes?.data || []);
    } catch (err) {
      console.error('Error cargando datos:', err);
      setError('Error al cargar datos necesarios');
    } finally {
      setCargandoDatos(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const agregarEquipoViejo = (equipo) => {
    if (!equiposViejos.find(e => e.sn === equipo.sn)) {
      setEquiposViejos([...equiposViejos, {
        sn: equipo.sn,
        motivo: ''
      }]);
    }
    setSelectedEquipoViejo(null);
    setSearchEquipoViejo('');
  };

  const eliminarEquipoViejo = (index) => {
    setEquiposViejos(equiposViejos.filter((_, i) => i !== index));
  };

  const actualizarMotivoViejo = (index, motivo) => {
    const nuevos = [...equiposViejos];
    nuevos[index].motivo = motivo;
    setEquiposViejos(nuevos);
  };

  const checkAvailability = async (dateTime) => {
    if (!formData.assigned_technician_id || !dateTime) {
      setIsAvailable(null);
      return;
    }

    setCheckingAvailability(true);
    setAvailabilityError('');
    setIsAvailable(null);
    setAvailableSlots([]);

    try {
      const result = await scheduleAPI.checkAvailability(
        formData.assigned_technician_id,
        dateTime,
        120
      );
      setIsAvailable(result.available);
      if (!result.available) {
        setAvailabilityError('El técnico no está disponible en este horario');
        loadAvailableSlots(dateTime);
      }
    } catch (err) {
      console.error('Error checking availability:', err);
      setAvailabilityError('Error al verificar disponibilidad');
    } finally {
      setCheckingAvailability(false);
    }
  };

  const loadAvailableSlots = async (dateTime) => {
    if (!formData.assigned_technician_id || !dateTime) return;
    try {
      const result = await scheduleAPI.getAvailableSlots(
        formData.assigned_technician_id,
        dateTime,
        120
      );
      setAvailableSlots(result.available_slots || []);
    } catch (err) {
      console.error('Error loading available slots:', err);
    }
  };

  const handleDateTimeChange = (e) => {
    const dateTime = e.target.value;
    setFormData(prev => ({ ...prev, scheduled_date: dateTime }));
    if (dateTime && formData.assigned_technician_id) {
      checkAvailability(dateTime);
    } else {
      setIsAvailable(null);
      setAvailabilityError('');
      setAvailableSlots([]);
    }
  };

  const selectAlternativeSlot = (slot) => {
    const dateTime = new Date(slot.start_time);
    const formattedDateTime = dateTime.toISOString().slice(0, 16);
    setFormData(prev => ({ ...prev, scheduled_date: formattedDateTime }));
    checkAvailability(formattedDateTime);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.customer_id) {
      setError('Debe seleccionar un cliente');
      return;
    }
    if (equiposViejos.length === 0) {
      setError('Debe seleccionar al menos un equipo a reemplazar');
      return;
    }
    if (!formData.assigned_technician_id) {
      setError('Debe seleccionar el técnico que realizará el cambio');
      return;
    }
    if (!formData.contact_phone) {
      setError('Debe ingresar un teléfono de contacto');
      return;
    }
    if (formData.scheduled_date && isAvailable !== true) {
      setError('Debe seleccionar un horario disponible para el técnico');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await ticketsAPI.createCambioEquipoTicket({
        customer_id: parseInt(formData.customer_id),
        equipos_viejos: equiposViejos,
        assigned_technician_id: parseInt(formData.assigned_technician_id),
        motivo_cambio: formData.motivo_cambio,
        title: formData.title || `Cambio de equipos - ${selectedCliente?.nombres} ${selectedCliente?.apellidos}`,
        description: formData.description,
        priority: formData.priority,
        contact_phone: formData.contact_phone,
        contact_phone_alt: formData.contact_phone_alt || null,
        scheduled_date: formData.scheduled_date || null
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/tickets');
      }, 2000);
    } catch (err) {
      console.error('Error creating ticket:', err);
      setError(err.response?.data?.detail || 'Error al crear el ticket de cambio de equipo');
    } finally {
      setLoading(false);
    }
  };

  if (cargandoDatos) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Cargando datos...</Typography>
      </Box>
    );
  }

  const equiposViejosFiltrados = equiposViejosDisponibles.filter(e =>
    e.sn?.toLowerCase().includes(searchEquipoViejo.toLowerCase()) ||
    e.modelo?.toLowerCase().includes(searchEquipoViejo.toLowerCase())
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Ticket de Cambio de Equipo
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Crear una orden de trabajo para reemplazar equipos. Seleccione los equipos a reemplazar.
        <br />Los equipos nuevos se seleccionarán al cerrar el ticket.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                1. Seleccionar Cliente
              </Typography>

              <FormControl fullWidth required sx={{ mb: 2 }}>
                <InputLabel>Cliente</InputLabel>
                <Select
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleChange}
                  label="Cliente"
                >
                  <MenuItem value="">Seleccione un cliente</MenuItem>
                  {clientes.map(cliente => (
                    <MenuItem key={cliente.id} value={cliente.id}>
                      {cliente.nombres} {cliente.apellidos} - {cliente.identificacion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedCliente && (
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="subtitle2">Cliente seleccionado:</Typography>
                  <Typography variant="body2">
                    {selectedCliente.nombres} {selectedCliente.apellidos}
                  </Typography>
                  <Typography variant="caption">
                    📞 {selectedCliente.telefono} | 📍 {selectedCliente.direccion}
                  </Typography>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary.main">
                2. Técnico Asignado
              </Typography>

              <FormControl fullWidth required sx={{ mb: 2 }}>
                <InputLabel>Técnico</InputLabel>
                <Select
                  name="assigned_technician_id"
                  value={formData.assigned_technician_id}
                  onChange={handleChange}
                  label="Técnico"
                >
                  <MenuItem value="">Seleccione un técnico</MenuItem>
                  {tecnicos.map(tecnico => (
                    <MenuItem key={tecnico.id} value={tecnico.id}>
                      {tecnico.nombre || tecnico.username || tecnico.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedTecnico && (
                <Paper sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                  <Typography variant="subtitle2" color="primary.main">
                    Técnico seleccionado:
                  </Typography>
                  <Typography variant="body2">{selectedTecnico.nombre || selectedTecnico.username}</Typography>
                  <Typography variant="caption">📧 {selectedTecnico.email}</Typography>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Accordion expanded={expandedViejos} onChange={() => setExpandedViejos(!expandedViejos)}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6" color="error.main">
                    📤 Equipos a REEMPLAZAR ({equiposViejos.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    fullWidth
                    placeholder="Buscar equipo por SN o modelo..."
                    size="small"
                    value={searchEquipoViejo}
                    onChange={(e) => setSearchEquipoViejo(e.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                  <List dense sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
                    {equiposViejosFiltrados.map(equipo => (
                      <ListItem
                        key={equipo.id}
                        button
                        onClick={() => agregarEquipoViejo(equipo)}
                        disabled={equiposViejos.some(e => e.sn === equipo.sn)}
                      >
                        <ListItemText primary={equipo.sn} secondary={equipo.modelo} />
                        {equiposViejos.some(e => e.sn === equipo.sn) && (
                          <Chip label="Agregado" color="success" size="small" />
                        )}
                      </ListItem>
                    ))}
                  </List>
                  <Divider />
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Equipos seleccionados:</Typography>
                  {equiposViejos.map((eq, idx) => (
                    <Paper key={idx} sx={{ p: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography fontWeight="bold">{eq.sn}</Typography>
                        <MuiIconButton size="small" color="error" onClick={() => eliminarEquipoViejo(idx)}>
                          <Delete />
                        </MuiIconButton>
                      </Box>
                      <TextField
                        fullWidth
                        size="small"
                        label="Motivo del reemplazo"
                        value={eq.motivo}
                        onChange={(e) => actualizarMotivoViejo(idx, e.target.value)}
                        placeholder="Ej: Equipo dañado, actualización..."
                      />
                    </Paper>
                  ))}
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Teléfono de Contacto"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Teléfono Alternativo"
                    name="contact_phone_alt"
                    value={formData.contact_phone_alt}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Prioridad</InputLabel>
                    <Select name="priority" value={formData.priority} onChange={handleChange} label="Prioridad">
                      <MenuItem value="baja">Baja</MenuItem>
                      <MenuItem value="media">Media</MenuItem>
                      <MenuItem value="alta">Alta</MenuItem>
                      <MenuItem value="critica">Crítica</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label="Fecha Programada"
                    name="scheduled_date"
                    value={formData.scheduled_date}
                    onChange={handleDateTimeChange}
                    InputLabelProps={{ shrink: true }}
                    disabled={!formData.assigned_technician_id}
                    helperText={!formData.assigned_technician_id ? "Primero seleccione un técnico" : ""}
                    error={!!availabilityError}
                  />
                  {checkingAvailability && <CircularProgress size={20} sx={{ mt: 1 }} />}
                  {isAvailable === true && formData.scheduled_date && (
                    <Alert severity="success" sx={{ mt: 1 }}>✅ Técnico disponible</Alert>
                  )}
                  {availabilityError && <Alert severity="error" sx={{ mt: 1 }}>{availabilityError}</Alert>}
                  {availableSlots.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption">Horarios sugeridos:</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        {availableSlots.slice(0, 3).map((slot, idx) => (
                          <Chip key={idx} label={new Date(slot.start_time).toLocaleTimeString()} onClick={() => selectAlternativeSlot(slot)} size="small" clickable />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Motivo del Cambio"
                    name="motivo_cambio"
                    value={formData.motivo_cambio}
                    onChange={handleChange}
                    required
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Título"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descripción"
                    name="description"
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: '#fff3e0' }}>
                    <Typography variant="body2" color="warning.main">
                      <strong>📋 Proceso de Cambio:</strong>
                      <br />• El técnico asignado deberá seleccionar el/los equipos nuevos al cerrar el ticket
                      <br />• Solo podrá usar equipos que tenga en su poder (EN_TRANSITO)
                      <br />• Los equipos viejos cambiarán a estado <strong>RETIRO_PENDIENTE</strong>
                      <br />• Los equipos nuevos cambiarán a estado <strong>INSTALADO</strong>
                      <br />• Los materiales utilizados se descontarán del stock del técnico
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || !formData.customer_id || equiposViejos.length === 0 || !formData.assigned_technician_id || (formData.scheduled_date && isAvailable !== true)}
                    startIcon={loading ? <CircularProgress size={20} /> : <SwapHoriz />}
                    fullWidth
                    size="large"
                  >
                    {loading ? 'Creando...' : 'Crear Ticket de Cambio'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={success} autoHideDuration={3000} onClose={() => setSuccess(false)}>
        <Alert severity="success">Ticket de cambio de equipo creado exitosamente</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert severity="error">{error}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateCambioEquipoTicket;
